import React, { useState, useEffect, useCallback, useReducer } from 'react';
import {
    Button,
    Select,
    Form,
    Input,
    Card,
    Spin,
    Alert,
    Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from 'NotificationContext';

const { TextArea } = Input;

function formatErrorData(payload) {
    const MAX_LENGTH = 500;

    if (typeof payload === 'string') {
        return payload.length > MAX_LENGTH ? "Description too long to display" : payload;
    }

    if (Array.isArray(payload)) {
        const errorString = payload.join('; ');
        return errorString.length > MAX_LENGTH ? "Description too long to display" : errorString;
    }

    if (typeof payload === 'object' && payload !== null) {
        const errorString = Object.entries(payload)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
        return errorString.length > MAX_LENGTH ? "Description too long to display" : errorString;
    }

    return "Unknown error format";
}

const crudActionTypes = {
    "ADD": "ADD",
    "ADD_SUCCESS": "ADD_SUCCESS",
    "ADD_FAILURE": "ADD_FAILURE",
    "EDIT": "EDIT",
    "EDIT_SUCCESS": "EDIT_SUCCESS",
    "EDIT_FAILURE": "EDIT_FAILURE",
}

const crudInitialState = {
    loading: false,
    error: null,
    success: null,
    error_data: "",
}

const crudReducer = (state, action) => {
    switch (action.type) {
        case crudActionTypes.ADD: {
            return {
                loading: true,
                error: null,
                success: null,
                error_data: ""
            }
        }
        case crudActionTypes.ADD_SUCCESS: {
            return {
                loading: false,
                error: false,
                success: true,
                error_data: ""
            }
        }
        case crudActionTypes.ADD_FAILURE: {
            return {
                loading: false,
                error: true,
                success: false,
                error_data: formatErrorData(action.payload)
            }
        }
        case crudActionTypes.EDIT: {
            return {
                loading: true,
                error: null,
                success: null,
                error_data: ""
            }
        }
        case crudActionTypes.EDIT_SUCCESS: {
            return {
                loading: false,
                error: false,
                success: true,
                error_data: ""
            }
        }
        case crudActionTypes.EDIT_FAILURE: {
            return {
                loading: false,
                error: true,
                success: false,
                error_data: formatErrorData(action.payload)
            }
        }
        default: {
            return state;
        }
    }
}

const statusOptions = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Archived', value: 'ARCHIVED' },
];

const ContractTypeForm = () => {
    // Placeholders the template can use; filled from the contract on export.
    const [placeholders, setPlaceholders] = useState([]);
    useEffect(() => {
        actions.loadContractPlaceholders()
            .then((response) => setPlaceholders(response.data?.placeholders || []))
            .catch(() => setPlaceholders([]));
    }, []);

    const { id } = useParams();
    const navigate = useNavigate();
    const [dataSource, setDataSource] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const notify = useNotification();
    const [crudState, crudDispatch] = useReducer(crudReducer, crudInitialState);

    const fetchContractTypeInfo = useCallback(async () => {
        if (id) {
            const response = await actions.fetchContractType(id);
            if (response) {
                setDataSource(response);
            }
        } else {
            setDataSource({ status: 'DRAFT' });
        }
        setLoading(false);
    }, [id]);

    useEffect(() => {
        fetchContractTypeInfo();
    }, [fetchContractTypeInfo]);

    const onFinish = async (values) => {
        if (values) {
            if (id) {
                crudDispatch({ type: crudActionTypes.EDIT });
                try {
                    await actions.updateContractType(values, id);
                    crudDispatch({ type: crudActionTypes.EDIT_SUCCESS });
                } catch (err) {
                    crudDispatch({ type: crudActionTypes.EDIT_FAILURE, payload: err?.response?.data });
                }
            } else {
                crudDispatch({ type: crudActionTypes.ADD });
                try {
                    await actions.addContractType(values);
                    crudDispatch({ type: crudActionTypes.ADD_SUCCESS });
                } catch (err) {
                    crudDispatch({ type: crudActionTypes.ADD_FAILURE, payload: err?.response?.data });
                }
            }
        }
    };

    useEffect(() => {
        if (crudState.loading === false) {
            if (crudState.error === true) {
                notify("error", id ? "Edit operation failed" : "Add operation failed", `${crudState.error_data}`);
            } else if (crudState.success === true) {
                notify("success", id ? "Contract Type updated successfully" : "Contract Type created successfully");
                navigate("/contract-type");
            }
        }
    }, [crudState.loading, crudState.error, crudState.success, crudState.error_data, id, navigate, notify]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 20, maxWidth: 800, margin: '0 auto' }}>
            <Card title={id ? "Edit Contract Type" : "Add Contract Type"} bordered={false}>
                <Form
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={dataSource}
                    disabled={crudState.loading}
                >
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter contract type name' }]}
                    >
                        <Input placeholder="Enter contract type name" />
                    </Form.Item>

                    <Form.Item
                        label="Status"
                        name="status"
                        rules={[{ required: true, message: 'Please select status' }]}
                    >
                        <Select
                            placeholder="Select status"
                            options={statusOptions}
                        />
                    </Form.Item>

                    <Form.Item
                        label="HTML Template Code"
                        name="html_code"
                        help="Optional: Enter HTML template code for this contract type"
                    >
                        <TextArea
                            rows={10}
                            placeholder="Enter HTML template code (optional)"
                        />
                    </Form.Item>

                    {placeholders.length > 0 && (
                        <Alert
                            type="info"
                            style={{ marginBottom: 16 }}
                            message="Placeholders — filled in from each contract when it is exported to PDF"
                            description={
                                <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 16, rowGap: 2 }}>
                                    {placeholders.map((item) => (
                                        <React.Fragment key={item.key}>
                                            <Typography.Text code copyable>{`{{ ${item.key} }}`}</Typography.Text>
                                            <Typography.Text type="secondary">{item.description}</Typography.Text>
                                        </React.Fragment>
                                    ))}
                                </div>
                            }
                        />
                    )}

                    <Form.Item>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate("/contract-type")}
                            style={{ marginRight: 10 }}
                        >
                            Back to Contract Types
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={crudState.loading}
                            style={{ marginRight: 10 }}
                        >
                            {id ? 'Update' : 'Create'}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ContractTypeForm;
